/**
 * Clair motion layer. Runs only when snippets/clair-motion.liquid added the
 * `clair-motion` class (motion allowed). Tags elements for scroll reveals,
 * staggers siblings, drives the hero parallax, magnetic buttons and the cart
 * icon bump. Section re-renders and infinite scroll are picked up through a
 * MutationObserver.
 */

const root = document.documentElement;

/** Selector → reveal style. Order matters: the first match wins. */
const REVEAL_TARGETS = [
  ['.clair-section:not(.clair-hero) .clair-display', 'up'],
  ['.clair-section:not(.clair-hero) .clair-eyebrow', 'up'],
  ['.clair-head__text, .clair-head__aside', 'up'],
  ['.clair-card', 'scale'],
  ['.clair-values__item', 'up'],
  ['.clair-faq__intro > *, .clair-faq__item', 'up'],
  ['.clair-collection-hero__inner > *', 'up'],
  ['.product-grid .product-grid__item, .product-grid > li', 'scale'],
  ['rte-formatter > h2, rte-formatter > p, rte-formatter > ul, rte-formatter > ol, rte-formatter > table, .rte > h2, .rte > p, .rte > ul', 'up'],
];

/**
 * Everything else on any page (theme sections, info pages, product pages, footer).
 * These only animate when they start below the fold, so nothing visible on load flickers.
 */
const GENERIC_TARGETS = [
  ['main .text-block', 'up'],
  ['main h1, main h2, main h3', 'up'],
  ['main [class*="ai-"] p, main [class*="ai-"] li', 'up'],
  ['main .image-block, main [class*="ai-"] img, main .product-information__media', 'mask'],
  ['main accordion-custom, main .product-recommendations .product-card, main .resource-card, main .blog-post-card', 'scale'],
  ['main .button, main .contact-form, main form', 'up'],
  ['footer .custom-section-content > * > *, footer .utilities > *', 'up'],
];

const STAGGER_LIMIT = 6;

function init() {
  if (!root.classList.contains('clair-motion')) return;
  root.classList.add('clair-motion-ready');

  const observer = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  /**
   * @param {ParentNode} scope
   * @param {boolean} [initial]
   */
  const tag = (scope, initial = false) => {
    const fold = window.innerHeight * 0.92;
    const targets = [
      ...REVEAL_TARGETS.map(([selector, style]) => [selector, style, false]),
      ...GENERIC_TARGETS.map(([selector, style]) => [selector, style, true]),
    ];
    for (const [selector, style, generic] of targets) {
      for (const element of scope.querySelectorAll(String(selector))) {
        if (!(element instanceof HTMLElement) || element.dataset.clairReveal) continue;
        if (element.closest('header, dialog, .clair-hero, [data-clair-reveal], .product-card__content, .menu-drawer, .sticky-add-to-cart')) continue;
        if (element.parentElement?.closest('details:not([open])')) continue;
        // Skip anything not currently laid out (hidden galleries, closed panels, fixed bars).
        if (!element.getClientRects().length || getComputedStyle(element).position === 'fixed') continue;
        if (!generic && element.closest('footer')) continue;
        if (generic) {
          if (element.closest('.clair-section') || element.querySelector('[data-clair-reveal]')) continue;
          if (element.closest('.product-details') && !element.matches('accordion-custom')) continue;
          if (initial && element.getBoundingClientRect().top < fold) continue;
          if (element.offsetHeight < 8) continue;
        }
        element.dataset.clairReveal = String(style);
        const siblings = element.parentElement
          ? [...element.parentElement.children].filter((child) => child.matches(selector))
          : [];
        const index = Math.max(0, siblings.indexOf(element));
        element.style.setProperty('--clair-i', String(index % STAGGER_LIMIT));
        observer.observe(element);
      }
    }
    for (const button of scope.querySelectorAll('.button, .clair-pill, .clair-quiz__open')) {
      if (button instanceof HTMLElement && !button.closest('header, dialog, .menu-drawer')) magnetic(button);
    }
  };

  tag(document, true);
  splitHeroWords();
  readingProgress();
  heroParallax();
  cartBump();

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) tag(node.parentElement || node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // Backup alongside the observer: some in-app and background browsers pause it,
  // so a cheap scroll check also reveals anything that has reached the viewport.
  let lastCheck = 0;
  const revealVisible = () => {
    lastCheck = Date.now();
    const limit = window.innerHeight * 0.95;
    for (const element of document.querySelectorAll('[data-clair-reveal]:not(.is-in)')) {
      const rect = element.getBoundingClientRect();
      if (rect.top < limit && rect.bottom > 0) reveal(/** @type {HTMLElement} */ (element));
    }
  };
  window.addEventListener(
    'scroll',
    () => {
      if (Date.now() - lastCheck > 120) revealVisible();
    },
    { passive: true }
  );
  window.addEventListener('scrollend', revealVisible, { passive: true });
  setTimeout(revealVisible, 1200);

  /** @param {HTMLElement} element */
  function reveal(element) {
    element.classList.add('is-in');
    observer.unobserve(element);
    // Drop the stagger delay once revealed so hover effects respond instantly.
    setTimeout(() => element.style.setProperty('--clair-i', '0'), 1600);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  function onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) reveal(/** @type {HTMLElement} */ (entry.target));
    }
  }
}

/** Thin progress bar at the top of blog articles. */
function readingProgress() {
  const main = document.querySelector('main');
  if (!main || !String(main.dataset.template || '').includes('article')) return;
  const bar = document.createElement('div');
  bar.className = 'clair-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.append(bar);
  const update = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.setProperty('--clair-progress', String(max > 0 ? Math.min(window.scrollY / max, 1) : 0));
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/** Wraps each word of the hero headline so it can rise in one after another. */
function splitHeroWords() {
  const title = document.querySelector('.clair-hero__title');
  if (!(title instanceof HTMLElement) || title.dataset.clairSplit) return;
  title.dataset.clairSplit = 'true';
  let index = 0;
  const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const nodes = [];
  while (walker.nextNode()) nodes.push(/** @type {Text} */ (walker.currentNode));
  for (const node of nodes) {
    const parts = (node.textContent || '').split(/(\s+)/);
    const fragment = document.createDocumentFragment();
    for (const part of parts) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        fragment.append(part);
        continue;
      }
      const span = document.createElement('span');
      span.className = 'clair-hero__word';
      span.style.setProperty('--clair-w', String(index++));
      span.textContent = part;
      fragment.append(span);
    }
    node.replaceWith(fragment);
  }
}

/** Slow parallax on the hero photo while the hero is on screen. */
function heroParallax() {
  const media = document.querySelector('.clair-hero__media');
  const image = media?.querySelector('img');
  if (!(media instanceof HTMLElement) || !(image instanceof HTMLElement)) return;
  let frame = 0;
  const update = () => {
    frame = 0;
    const rect = media.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;
    const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
    image.style.setProperty('--clair-parallax', `${(progress * -40).toFixed(1)}px`);
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!frame) frame = requestAnimationFrame(update);
    },
    { passive: true }
  );
  update();
}

/**
 * Buttons gently follow the pointer, then spring back.
 * @param {HTMLElement} element
 */
function magnetic(element) {
  if (element.dataset.clairMagnetic || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  element.dataset.clairMagnetic = 'true';
  element.addEventListener('pointermove', (event) => {
    const rect = element.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
    element.style.setProperty('--clair-mx', `${x.toFixed(1)}px`);
    element.style.setProperty('--clair-my', `${y.toFixed(1)}px`);
  });
  element.addEventListener('pointerleave', () => {
    element.style.setProperty('--clair-mx', '0px');
    element.style.setProperty('--clair-my', '0px');
  });
}

/** Pops the cart icon whenever the cart changes. */
function cartBump() {
  document.addEventListener('cart:update', () => {
    for (const icon of document.querySelectorAll('.header-actions__cart-icon')) {
      icon.classList.remove('clair-bump');
      void (/** @type {HTMLElement} */ (icon).offsetWidth);
      icon.classList.add('clair-bump');
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
